"use client"

import type { ReactNode } from "react"
import Link from "next/link"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "~/components/ui/dialog"
import { Button } from "~/components/ui/button"
import { SignupIncentiveNote } from "./SignupIncentive"

type AuthPromptDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: ReactNode
  icon?: string
  /**
   * #1420 — some callers' `description` already spells out the Path Coins
   * pitch verbatim (the admin-managed fee-waiver box would otherwise repeat
   * it in different words right below). Defaults to false; every other
   * caller is unaffected.
   */
  hideIncentiveNote?: boolean
}

export function AuthPromptDialog({
  open,
  onOpenChange,
  title,
  description,
  icon,
  hideIncentiveNote = false,
}: AuthPromptDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            {icon && <span className="text-2xl">{icon}</span>}
            {title}
          </DialogTitle>
          <DialogDescription className="text-base text-gray-600 pt-1">
            {description}
          </DialogDescription>
        </DialogHeader>
        {!hideIncentiveNote && <SignupIncentiveNote className="mt-1" />}
        <div className="flex flex-col gap-3 pt-2">
          <Button
            asChild
            className="bg-[#DE866D] hover:bg-[#C97257] text-white w-full"
          >
            <Link href="/signup">Create Free Account</Link>
          </Button>
          <Button variant="outline" asChild className="w-full">
            <Link href="/loginpage">Log In</Link>
          </Button>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="w-full text-gray-500"
          >
            Continue browsing
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
